// 신호 프로파일 — **이 파일의 값은 계약이 아니다**
//
// 정본이 계약하는 것은 성질 셋이다: 셀 수 있을 것, 종점이 단 하나일 것, 오래 들어도
// 괴롭지 않을 것. 그 성질을 만족시키는 소리의 실제 값 — 표지가 몇 개인지, 어느 음고인지,
// 몇 밀리초인지 — 은 계약이 아니라 **프로파일**이고, 사람이 실기기에서 듣고 아니라고
// 하면 정본을 열지 않고 이 파일만 고친다.
//
// 이렇게 층을 가른 이유는 하나다. 2026-08-05에 형태(연속음이 끊기는 순간이 개시)를
// 계약에 못박았는데 2026-08-07에 사람의 귀가 그것을 물렸고, 그 순간 정본 여섯 자리와
// 판정 계약과 채점기가 함께 흔들렸다. 값을 이 파일에 몰아 두면 다음 청취에서 흔들리는
// 것은 이 파일 하나뿐이다.
//
// **사람이 읽는 사본은 구현 계획서의 오디오 프로브 할 일에 표로 있다.** 사본과 이 파일이
// 어긋나면 이 파일이 이긴다.
//
// ── 프로파일에서도 건드릴 수 없는 값 하나 ─────────────────────────────────
//
// **구간의 길이 5.000초.** 첫 표지의 온셋에서 종점의 온셋까지가 정확히 5초여야
// 발사 하한 6초(5초 + 여유 1초)의 근거가 선다. 하한은 정본과 판정 계약과 1차 범위
// 항목이 함께 붙들고 있는 값이라, 이 5초가 움직이면 셋이 같이 무너진다.
// 아래 profileViolations 가 그것을 기계로 막는다.

export type SignalRole = 'marker' | 'endpoint'

export interface SignalEvent {
  readonly role: SignalRole
  /** 개시(T=0) 기준 온셋. 음수가 개시 전이다. */
  readonly onsetSec: number
  readonly frequencyHz: number
  /** 온셋부터 최대 진폭을 유지하는 시간. 이 뒤에 releaseSec 동안 0으로 내린다. */
  readonly durationSec: number
  /** 소리가 멎는 자리의 램프 길이. 0이면 계단 불연속이 생긴다. */
  readonly releaseSec: number
  /** 이 이벤트의 최대 진폭. 풀스케일(1)에 닿으면 헤드룸이 0이다. */
  readonly peak: number
}

export interface SignalProfile {
  readonly name: string
  /**
   * 온셋 램프 길이. **목표 시각을 이 램프 한가운데 둔다** — 게인 0이 목표보다
   * attackSec/2 앞, 최대가 그만큼 뒤다. 진폭이 절반에 닿는 지점이 정확히 목표 시각이고,
   * 기계 판정도 사람의 귀도 그 지점을 온셋으로 읽는다.
   */
  readonly attackSec: number
  /** 표지 사이의 간격. 등간격이라야 앞선 표지로 다음 표지를 예측할 수 있다. */
  readonly markerIntervalSec: number
  /** 구간 안에서 소리가 나는 시간의 비율 상한. */
  readonly dutyCeiling: number
  readonly waveform: 'sine'
  readonly events: readonly SignalEvent[]
}

/** 계약이 못박은 구간의 길이. 첫 표지 온셋에서 종점 온셋까지다. */
export const SIGNAL_WINDOW_SEC = 5

const MARKER: Omit<SignalEvent, 'onsetSec'> = {
  role: 'marker',
  frequencyHz: 880,
  durationSec: 0.06,
  releaseSec: 0.008,
  peak: 0.35,
}

/**
 * 「다섯 셈 · 출발」 — 1초 간격 표지 다섯과 개시 순간의 출발음 하나.
 *
 * **이 값 묶음은 2026-08-10 사람 청취를 통과했다가 2026-08-14에 한 자리를 물렸다.**
 * 2026-08-10 사용자 원문은 「신호는 명확하고 시끄럽지 않으며 정확하게 5초간 rally 를 open
 * 할 수 있도록 가이드 하고 있다. 확정한다」이고, 그 한 문장이 성질 셋에 하나씩 대응했다 —
 * 명확하다(종점이 단 하나), 시끄럽지 않다(오래 들어도 괴롭지 않다), 5초간 가이드한다(셀 수
 * 있다).
 *
 * **그런데 그 청취는 실기기가 아니었다.** 2026-08-14에 사용자가 폰으로 IP 직결 HTTP 에
 * 붙어 다시 듣고 「사람마다 누르는 시간이 다르다」를 확인했고, 그것이 첫째 대응(종점이 단
 * 하나)을 뒤집었다. 나머지 둘은 같은 청취에서 다시 확인됐다. **그러므로 아래 숫자들은
 * 대부분 귀로 검증된 값이고, 종점의 길이 둘만 아직 가설이다.** 고치기 전에 어느 쪽인지
 * 보고 고칠 것 — 이력은 아래 「종점은 왜 짧아야 하는가」 절에 있다.
 *
 * 그리고 이 통과가 **정본을 한 글자도 열지 않고** 났다는 것이 층을 성질과 프로파일로
 * 가른 목적이 실제로 값을 한 자리다. 2026-08-05의 형태 채택은 정본 여섯 자리와 판정
 * 계약과 채점기를 함께 흔들었고, 2026-08-07의 회수도 그랬다.
 *
 * 표지 다섯이 **전부 같은** 이유는 그래야 메트로놈이 되고, 메트로놈이라야 몸이 박자를
 * 잡기 때문이다. 표지마다 음을 다르게 만들면 「셀 수 있을 것」이 요구하는 등간격 동일
 * 자극이 깨진다. **표지 하나만 따로 들은 귀가 그것이 몇 번째인지 모르는 것은 흠이
 * 아니다** — 남은 초의 위치는 화면이 나르고, 소리는 박자와 종점만 나른다.
 *
 * 출발음이 표지와 **두 축에서** 다른 이유는 한 축만 다르면 그 축을 놓친 귀에 같은
 * 소리가 되기 때문이다. 음고는 한 옥타브 위(2배), 길이는 2배, 그리고 진폭이 1.43배다.
 *
 * ── 종점은 왜 짧아야 하는가 (2026-08-14 사람 청취) ────────────────────────
 *
 * **길이가 6.7배이던 것을 2배로 줄였다.** 사용자가 실기기로 듣고 「사람마다 누르는 시간이
 * 다르다」를 확인했기 때문이고, 그것은 성질 둘(종점이 단 하나일 것)이 무너졌다는 뜻이다.
 * 같은 청취에서 소리가 실제로 나는 것과 박자로 개시를 미리 짚을 수 있는 것은 확인됐다 —
 * **무너진 것은 셋 중 하나뿐이고, 그 하나가 이 값이었다.**
 *
 * 무엇이 둘로 읽혔는가. 옛 값은 유지 0.4초에 감쇠 0.06초라 **소리가 460밀리초** 났다.
 * 표지는 68밀리초여서 시작과 끝이 한 덩어리로 뭉쳐 들리는데, 460밀리초는 그 둘이 떨어져
 * **두 사건**이 된다. 그래서 어떤 귀는 소리가 날 때를, 어떤 귀는 끝날 때를 개시로 읽고,
 * 그 차이가 0.46초다. 이 제품에서 0.46초는 rally 하나가 어긋나는 폭이다.
 *
 * **이 파일은 그 실패를 미리 알고도 막지 못했다.** 아래 오프셋 램프 자리에 「새 형태에서
 * 개시는 어떤 소리의 오프셋도 아니다」라고 의도를 적어 두었는데, 460밀리초에서는 그 의도가
 * 값으로 지켜지지 않았다. `scripts/live.sh` 도 「개시 뒤에 남는 꼬리가 도움인지 방해인지
 * 이 자리에는 아직 아무 근거가 없다」고 열어 두었고, 이번 청취가 그 첫 근거다. 그리고
 * 2026-08-05에 회수된 옛 형태가 「연속음이 끊기는 순간이 개시」였으므로, **끝을 신호로
 * 읽는 습관 자체가 이 제품의 이력에 있다.**
 *
 * 고치는 방향을 길이로 잡은 이유는, 종점을 표지와 가르는 축 셋 중 **길이만이 시간을
 * 번지게 하기** 때문이다. 음고와 진폭은 사건을 한 점에 두고도 다르게 만들 수 있지만,
 * 길이는 다르게 만드는 그 방식으로 사건을 늘인다. 그래서 길이는 「구별될 만큼만」 두고
 * 구별의 무게는 음고와 진폭에 싣는다.
 *
 * **2026-08-14에 사용자가 이 값을 채택했다** — 원문은 「좋아. 채택해. 140 밀리초도 좋아」다.
 * 같은 날 같은 사람의 실기기 청취가 앞의 값을 물린 그 자리이고, 물린 관측과 채택한 값이
 * 하루 안에 이어져 있다.
 *
 * **다음 청취가 여전히 갈린다고 하면 길이를 더 줄이지 않는다.** 감쇠를 0에 가깝게 두면
 * 계단 불연속이라 딸깍 소리가 나고, 그 딸깍이 다시 두 번째 사건이 된다 — 줄이는 방향의
 * 끝에 같은 실패가 한 번 더 있다. 그때 볼 자리는 길이가 아니라 시간을 번지게 하지 않는
 * 다른 축(진폭 포락선·음고 이동)이고, 그 판단도 귀가 한다.
 *
 * 진폭이 풀스케일에 한참 못 미치는 이유는 규율이다. 회수된 형태는 이 값이 1.0이라
 * 헤드룸이 0이었다. 그것이 실기기에서 무엇을 만들었는지는 이 저장소에 측정값이 없으므로
 * 인과를 적지 않는다 — 다만 소음 감소의 절반이 형태가 아니라 이 값에서 나온다는 것은
 * 계산으로 아는 사실이다.
 */
export const SIGNAL_PROFILE: SignalProfile = {
  name: '다섯 셈 · 출발',
  attackSec: 0.006,
  markerIntervalSec: 1,
  // 이 표의 값으로는 7%가 나온다. 20%는 측정이나 문헌에서 나온 숫자가 아니라 표지를
  // 조금 늘리거나 종점을 늘려도 걸리지 않게 넉넉히 잡은 값이다. **검증되지 않은 값이
  // 검증된 값처럼 굳는 것이 이 파일에서 가장 위험한 자리라** 그 사실을 여기 적어 둔다.
  //
  // **2026-08-10 청취가 통과시킨 것은 이 표의 7%이지 상한 20%가 아니다.** 사람이 들은
  // 소리는 듀티 7%짜리 한 벌이고, 20%가 여전히 참을 만한지는 아무도 재지 않았다. 상한을
  // 근거로 표지를 늘리는 회차가 오면 그때 다시 들어야 한다.
  dutyCeiling: 0.2,
  // 사각파·톱니파의 홀수 고조파는 사람이 가장 날카롭게 느끼는 대역까지 뻗는다.
  // 880도 1760도 그 아래다.
  waveform: 'sine',
  events: [
    { ...MARKER, onsetSec: -5 },
    { ...MARKER, onsetSec: -4 },
    { ...MARKER, onsetSec: -3 },
    { ...MARKER, onsetSec: -2 },
    { ...MARKER, onsetSec: -1 },
    {
      role: 'endpoint',
      onsetSec: 0,
      frequencyHz: 1_760,
      // **2026-08-14 사람 청취가 이 두 값을 물렸다.** 0.4 + 0.06 이던 것을 줄였다.
      // 왜인지는 위 「종점은 왜 짧아야 하는가」 절에 있다 — 460밀리초는 시작과 끝이 두
      // 사건으로 갈리는 길이였고, 사람마다 그중 다른 쪽에 반응했다.
      durationSec: 0.12,
      releaseSec: 0.02,
      peak: 0.5,
    },
  ],
}

const EPSILON = 1e-9

function markers(profile: SignalProfile): SignalEvent[] {
  return profile.events.filter((e) => e.role === 'marker')
}

function endpointOf(profile: SignalProfile): SignalEvent | undefined {
  return profile.events.find((e) => e.role === 'endpoint')
}

/** 첫 표지의 온셋에서 종점의 온셋까지. 계약이 5.000초로 못박은 값이다. */
export function signalWindowSec(profile: SignalProfile): number {
  const first = markers(profile)[0]
  const endpoint = endpointOf(profile)
  if (first === undefined || endpoint === undefined) return 0
  return endpoint.onsetSec - first.onsetSec
}

/**
 * 구간 안에서 소리가 나는 시간의 비율. 구간 밖으로 삐져나가는 몫은 잘라 센다 —
 * 종점의 400밀리초는 구간이 끝난 뒤에 울리므로 이 비율에 거의 들어오지 않는다.
 */
export function profileDutyRatio(profile: SignalProfile): number {
  const window = signalWindowSec(profile)
  if (window <= 0) return 0
  const first = markers(profile)[0]
  if (first === undefined) return 0
  const from = first.onsetSec
  const to = from + window
  const half = profile.attackSec / 2

  let sounding = 0
  for (const event of profile.events) {
    const begins = Math.max(from, event.onsetSec - half)
    const ends = Math.min(to, event.onsetSec + event.durationSec + event.releaseSec)
    if (ends > begins) sounding += ends - begins
  }
  return sounding / window
}

/**
 * 프로파일이 성질 셋을 무너뜨리는 자리를 사람의 말로 돌려준다. 빈 배열이면 통과다.
 *
 * **값을 고치는 사람이 계약을 함께 고치지 않게 하려고 이 함수가 있다.** 값은 자유롭게
 * 바꿔도 되지만, 아래 여섯 가지를 어기는 값은 성질 셋 중 하나를 깬다.
 */
export function profileViolations(profile: SignalProfile): string[] {
  const problems: string[] = []
  const ms = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 })

  const window = signalWindowSec(profile)
  if (Math.abs(window - SIGNAL_WINDOW_SEC) > EPSILON) {
    problems.push(
      `구간 길이가 ${String(SIGNAL_WINDOW_SEC)}초가 아니다 (${ms.format(window * 1000)}밀리초). ` +
        '발사 하한 6초의 근거가 이 5초라 여기서 무너진다.',
    )
  }

  const onsets = markers(profile).map((e) => e.onsetSec)
  if (onsets.length < 2) {
    problems.push('표지가 둘 미만이라 간격이 서지 않는다. 격자가 없으면 예측이 불가능하다.')
  }
  for (let i = 1; i < onsets.length; i += 1) {
    const gap = (onsets[i] ?? 0) - (onsets[i - 1] ?? 0)
    if (Math.abs(gap - profile.markerIntervalSec) > EPSILON) {
      problems.push(
        `표지 ${String(i)}과 ${String(i + 1)}의 간격이 선언값과 다르다 ` +
          `(${ms.format(gap * 1000)}밀리초 대 ${ms.format(profile.markerIntervalSec * 1000)}밀리초). ` +
          '등간격이 깨지면 앞선 표지로 다음을 예측할 수 없다.',
      )
    }
  }

  for (const event of profile.events) {
    if (event.peak >= 1) {
      problems.push(
        `${event.role} 의 최대 진폭이 ${String(event.peak)} 이라 헤드룸이 0이다. ` +
          '풀스케일에 닿는 소리는 기기 믹서가 깎는다.',
      )
    }
    if (event.releaseSec <= 0) {
      problems.push(
        `${event.role} 에 오프셋 램프가 없다. 소리가 멎는 자리마다 계단 불연속이 생긴다.`,
      )
    }
  }

  const duty = profileDutyRatio(profile)
  if (duty > profile.dutyCeiling) {
    problems.push(
      `듀티가 상한을 넘는다 (${ms.format(duty * 100)}% 대 ${ms.format(profile.dutyCeiling * 100)}%). ` +
        '구간을 소리로 채우면 반복해 듣기 괴로워진다.',
    )
  }

  const endpoint = endpointOf(profile)
  const marker = markers(profile)[0]
  if (endpoint === undefined) {
    problems.push('종점이 없다. 개시를 가리키는 사건이 없으면 신호가 아니다.')
  } else if (marker !== undefined) {
    const differsInPitch = Math.abs(endpoint.frequencyHz - marker.frequencyHz) > EPSILON
    const differsInLength = Math.abs(endpoint.durationSec - marker.durationSec) > EPSILON
    if (!differsInPitch || !differsInLength) {
      problems.push(
        '종점이 표지와 두 축에서 다르지 않다. 한 축만 다르면 그 축을 놓친 귀에는 같은 소리다.',
      )
    }

    // **종점이 길어지는 방향에도 벽이 있다 (2026-08-14 사람 청취).**
    //
    // 위 검사는 「다르기만 하면 된다」라서 길수록 안전하다고 읽힌다. 그 읽기가 실제로
    // 물렸다 — 소리가 460밀리초 나던 종점에서 **시작과 끝이 두 사건으로 갈렸고**, 어떤
    // 귀는 소리가 날 때를 어떤 귀는 끝날 때를 개시로 읽었다. 성질 둘(종점이 단 하나)이
    // 무너지는 자리이고, 그 차이 0.46초는 rally 하나가 어긋나는 폭이다.
    //
    // 표지가 언제나 한 사건이었던 이유는 짧아서다. 종점도 같은 「한 블립」 범위에 있어야
    // 하고, 구별의 무게는 시간을 번지게 하지 않는 축(음고·진폭)이 진다.
    //
    // **3배는 재어서 나온 상수가 아니라 관측 뒤에 세운 난간이다.** 6.7배는 사람이 물렸고
    // 2배는 아직 판정 전이다. 다음 청취가 2배도 물리면 이 값을 좁힌다 — 늘리지 않는다.
    const soundingRatio =
      (endpoint.durationSec + endpoint.releaseSec) / (marker.durationSec + marker.releaseSec)
    if (soundingRatio > 3) {
      problems.push(
        `종점이 표지의 ${ms.format(soundingRatio)}배 길이다. ` +
          '이만큼 길면 시작과 끝이 두 사건으로 갈려 사람마다 다른 순간에 누른다.',
      )
    }
  }

  if (profile.waveform !== 'sine') {
    problems.push(
      `파형이 사인이 아니다 (${String(profile.waveform)}). ` +
        '홀수 고조파가 사람이 가장 날카롭게 느끼는 대역까지 뻗는다.',
    )
  }

  return problems
}
